import { ConfigService } from './../../base-services/config.service';
import { AuthenticationService } from './../../base-services/authentication.service';
import { ChangeDetectorRef, Component, Input } from "@angular/core";
import { NgbDropdownConfig } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { SignalRService } from '../../base-services/signalr.service';
import { Subject, combineLatest, from } from 'rxjs';
import { takeUntil, filter, map, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import * as pdfjsLib from 'pdfjs-dist';

(pdfjsLib as any).GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.js';
//zbog navođenja ove putanje, dodatne izmene u angular.json za klijentski deo, vezano za assets.

@Component({
  selector: 'yt-top-menu',
  templateUrl: './top-menu.component.html',
  providers: [NgbDropdownConfig]
})
export class TopMenu {
  constructor(
    private authenticationService: AuthenticationService,
    private ngbDropdownConfig: NgbDropdownConfig,
    private router: Router,
    private configService: ConfigService,
    private signalRService: SignalRService,
    private cd: ChangeDetectorRef
  ){
    ngbDropdownConfig.placement = 'bottom-right';
    ngbDropdownConfig.autoClose = true;
  }

  isAuthenticated: boolean = false;
  selectedOrgJedName: string = '';
  username: string = '';
  showSettingsButton: boolean = false;

  unreadNotificationsCount: number = 0;
  notifications: any[] = [];
  hasNewNotifications: boolean = false;

  private unsubscribe$ = new Subject<void>();
  private lastConnectedUserId: string | null = null;

  @Input() BazaZnanjaLink: string;

  ngOnInit() {

    window.addEventListener('StatusPromenjen', (e: any) => {
      const zahtevId = e.detail.zahtevId;
      const status = e.detail.status;
      this.oznaciNotifikacijuKaoObradjenu(zahtevId, status);
    });

    combineLatest([
      this.authenticationService.IsAuthenticated, 
      this.configService.SelectedUser          
    ])
    .pipe(
      takeUntil(this.unsubscribe$),

      tap(([isAuth]) => {
        this.isAuthenticated = isAuth;
        if (!isAuth) {
          this.safeDisconnect();
          this.lastConnectedUserId = null;
          this.notifications = [];
          this.unreadNotificationsCount = 0;
          this.hasNewNotifications = false;
          this.cd.detectChanges();
        }
      }),

      filter(([isAuth, user]) => {
        const uid = (user && user.UserID ? String(user.UserID) : '').trim();
        return !!isAuth && !!uid && /^\d+$/.test(uid);
      }),

      map(([_, user]) => String(user!.UserID).trim()),

      distinctUntilChanged(),

      switchMap((userId) => from(this.rebindForUser(userId)))
    )
    .subscribe({
      error: (err) => {  }
    });

    this.signalRService.notifications$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((notification: any) => {
        if (typeof notification.Parametri === 'string') {
          try {
            notification.Parametri = JSON.parse(notification.Parametri);
          } catch (e) {
            // console.warn('⚠️ Nevalidan JSON u notification.Parametri:', notification.Parametri);
            notification.Parametri = {};
          }
        }
        if (!notification.Parametri) {
          notification.Parametri = {};
        }

        let idx = -1;
        for (let i = 0; i < this.notifications.length; i++) {
          if (this.notifications[i].ID === notification.ID) { idx = i; break; }
        }

        if (idx !== -1) {
          const prev = this.notifications[idx];
          const wasUnread = !prev.Procitano;

          this.notifications[idx] = Object.assign({}, prev, notification);

          const nowUnread = !this.notifications[idx].Procitano;
          if (wasUnread && !nowUnread && this.unreadNotificationsCount > 0) {
            this.unreadNotificationsCount--;
          }

          const p = this.notifications[idx].Parametri || {};
          if (notification.ActionResult && p.ZahtevID) {
            this.oznaciNotifikacijuKaoObradjenu(p.ZahtevID, notification.ActionResult);
          } else {
            this.cd.detectChanges();
          }
          return;
        }

        this.notifications.push(notification);
        this.notifications.sort((a: any, b: any) => b.ID - a.ID);

        if (!notification.Procitano) {
          this.unreadNotificationsCount++;
        }
        this.hasNewNotifications = true;
        this.cd.detectChanges();

        if (notification.ActionResult && notification.Parametri && notification.Parametri.ZahtevID) {
          this.oznaciNotifikacijuKaoObradjenu(notification.Parametri.ZahtevID, notification.ActionResult);
        }
      });

    this.configService.SelectedOrgJed
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedOrgJed) => {
        this.selectedOrgJedName = this.getOrgJedName(selectedOrgJed);
      });

    this.configService.SelectedUser
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedUser) => {
        this.username = selectedUser ? selectedUser.Username : '';
      });
  }

  ngOnDestroy() {
    if (this.cd) this.cd.detach();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.safeDisconnect();
  }

  private async rebindForUser(userId: string): Promise<void> {
    if (this.lastConnectedUserId === userId) return;

    await this.safeDisconnect();

    await this.signalRService.startConnection(userId)
      .pipe(takeUntil(this.unsubscribe$))
      .toPromise()
      .then(async (connected) => {
        if (connected) {
          this.lastConnectedUserId = userId;
          this.signalRService.loadNotifications(userId);
        }
      });
  }

  private async safeDisconnect(): Promise<void> {
    try {
      if (this.signalRService && typeof this.signalRService.stopConnection === 'function') {
        this.signalRService.stopConnection();
      }
    } catch (e) {
      // console.warn('stopConnection() error (ignorisano):', e);
    }
  }

  logout(){
    this.authenticationService.Logout();
  }

  changeOrgJed(){
    this.router.navigate(['company-selector']);
  }

  showSettings(){
  }

  private getOrgJedName(selectedOrgJed){
    if(!selectedOrgJed){ return ''; }

    if(selectedOrgJed.FirmaNaziv === selectedOrgJed.Naziv){
      return this.setOrgJedNameLength(selectedOrgJed.Naziv, 50);
    }
    return this.setOrgJedNameLength(selectedOrgJed.FirmaNaziv, 20) + ', ' + this.setOrgJedNameLength(selectedOrgJed.Naziv, 20);
  }

  private setOrgJedNameLength(orgJedName: string, length: number){
    if(!orgJedName) return '';
    if(orgJedName.length <= length){ return orgJedName; }
    return orgJedName.substr(0, length - 3) + '...';
  }

  // openHelpDocument() {
  //   const basePath = "/assets/pdfs/manual.pdf";
  //   const urlSegments = this.router.url.split('/');
  //   const lastSegment = urlSegments[urlSegments.length - 1];
  //   const bookmark = lastSegment ? `#nameddest=${lastSegment}` : '';
  //   const pdfPath = `${basePath}${bookmark}`;
  //   window.open(pdfPath, '_blank');
  // }

  async openHelpDocument() {
    const basePath = '/assets/pdfs/manual.pdf';
    const urlSegments = this.router.url.split('/');
    const lastSegment = urlSegments[urlSegments.length - 1]; // npr. ulazniracuni

    try {
      const loadingTask = pdfjsLib.getDocument(basePath);
      const pdf = await loadingTask.promise;

      // 1) pokušaj named destination direktno
      // const allDests = await pdf.getDestinations();
      // console.log('all destinations:', allDests);
      const outline = await pdf.getOutline();
      // console.log('outline:', outline);
      const outlineNode = this.findOutlineNodeByRoute(outline || [], lastSegment);

      if (!outlineNode) {
        window.open(`${basePath}#page=1`, '_blank', 'noopener');
        return;
      }

      let dest = outlineNode.dest;

      if (typeof dest === 'string') {
        dest = await pdf.getDestination(dest);
      }

      if (Array.isArray(dest) && dest.length > 0) {
        const first = dest[0];
        let pageNumber = 1;

        if (typeof first === 'object' && first !== null) {
          const pageIndex = await pdf.getPageIndex(first);
          pageNumber = pageIndex + 1;
        } else if (typeof first === 'number') {
          pageNumber = first + 1;
        }

        window.open(`${basePath}#page=${pageNumber}`, '_blank', 'noopener');
        return;
      }

      // 2) fallback: nema named destination -> otvori prvu stranu
      window.open(`${basePath}#page=1`, '_blank', 'noopener');
    } catch (e) {
      console.error('Greška pri otvaranju PDF help-a', e);
      window.open(basePath, '_blank', 'noopener');
    }
  }

  private findOutlineNodeByRoute(outline: any[], routeKey: string): any | null {
  const normalizedRoute = this.normalize(routeKey);

  for (const node of outline) {
    const normalizedTitle = this.normalize(node.title);

    // exact
    if (normalizedTitle === normalizedRoute) {
      return node;
    }

    // contains, za slučaj "Ulazni računi" vs "racuni"
    if (normalizedTitle.includes(normalizedRoute)) {
      return node;
    }

    if (node.items && node.items.length) {
      const childMatch = this.findOutlineNodeByRoute(node.items, routeKey);
      if (childMatch) {
        return childMatch;
      }
    }
  }

  return null;
}

private normalize(value: string): string {
  return (value || '')
    .toLowerCase()
    .replace(/š/g, 's')
    .replace(/đ/g, 'dj')
    .replace(/č/g, 'c')
    .replace(/ć/g, 'c')
    .replace(/ž/g, 'z')
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9]/g, '');
}

  toggleReadStatus(notification: any): void {
    notification.Procitano = !notification.Procitano;
    this.signalRService.markAsRead(notification);
    this.unreadNotificationsCount = Math.max(
      0,
      this.unreadNotificationsCount + (notification.Procitano ? -1 : 1)
    );
  }

  handleNotificationAction(notification: any, action: string): void {
    if (notification._processing || notification.ActionResult) return;

    switch (notification.Tip) {
      case 'univerzalno':
        if (window['pozoviAkcijuSaAplikacije']) {
          window['pozoviAkcijuSaAplikacije']({
            action,
            zahtevId: notification.Parametri.ZahtevID
          });
        }
        break;

      case 'pdf':
        const url = notification.Parametri.url;
        if (url) {
          window.open(url, '_blank');
          this.signalRService.markAsRead(notification);
          notification.Procitano = true;
          this.unreadNotificationsCount = Math.max(0, this.unreadNotificationsCount - 1);
        }
        break;

      default:
        // console.warn(`Nepoznat tip notifikacije: ${notification.Tip} ili akcija: ${action}`);
        break;
    }
  }

  oznaciNotifikacijuKaoObradjenu(zahtevId: string, status: string): void {
    const n = this.notifications.find(x =>
      x && x.Parametri && String(x.Parametri.ZahtevID).trim() === String(zahtevId).trim()
    );
    if (!n) return;

    n.ActionResult = status;

    if (!n.Procitano) {
      n.Procitano = true;
      n.VremeProcitanaPoruka = new Date().toISOString();
      this.signalRService.markAsRead(n);
      this.unreadNotificationsCount = Math.max(0, this.unreadNotificationsCount - 1);
    }

    this.cd.detectChanges();
  }

  openBazaZnanja(){
    const url = this.BazaZnanjaLink || 'https://minibiz.rs/funkcionalnosti/';
    window.open(url, '_blank', 'noopener');
  }
}
